import { defineStore } from 'pinia'
import { readonly } from 'vue'

import { getBranchData } from '@/utils/api-service'
import { sleep } from '@/utils/time'

import type {
  LanguageTourCatalog,
  AllData,
  LanguageExhibits,
  LanguageStrings,
  MediaAsset,
  Settings,
  SupportedLanguage,
  LanguageSouvenirs,
} from 'types/data'
import type { DeepReadonly } from 'vue'

interface DataState {
  settings: DeepReadonly<Settings>
  languageStrings: DeepReadonly<LanguageStrings[]>
  languages: DeepReadonly<SupportedLanguage[]>
  languageExhibits: DeepReadonly<LanguageExhibits[]>
  languageTourCatalogs: DeepReadonly<LanguageTourCatalog[]>
  languageSouvenirs: DeepReadonly<LanguageSouvenirs[]>
  displaySyncUrl: string
  token: string
  serviceWorkerUrl: string
}

export const useDataStore = defineStore('data', {
  state: (): DataState => ({
    settings: readonly({
      environment: '',
      demoModeTags: '',
      previewMode: false,
      useBlobStorage: false,
      hybrid: false,
      mqtt: {
        brokerHost: '',
        brokerPort: 0,
        brokerWsPort: 0,
        password: '',
        clientLocalHost: null,
        username: '',
      },
      certificate: {
        cert: '',
        key: '',
        dev: false,
      },
      branch: {
        apiUrl: '',
        branchId: 0,
        displaySyncUrl: '',
      },
      hasContentHubTours: false,
      hasLocationSupport: false,
      logLocationChanges: false,
      logTagFilter: '',
      logTagPosition: false,
      disableSASTokens: false,
    }),
    languageStrings: readonly([]),
    languages: readonly([]),
    languageExhibits: readonly([]),
    languageTourCatalogs: readonly([]),
    languageSouvenirs: readonly([]),
    displaySyncUrl: '',
    token: '',
    serviceWorkerUrl: '',
  }),

  getters: {
    museumType (): 'traditional' | 'location' | 'hybrid' {
      return this.settings.hybrid
        ? 'hybrid'
        : this.settings.hasLocationSupport ? 'location' : 'traditional'
    },
    hasLocationSupport (): boolean {
      return this.museumType === 'hybrid' || this.museumType === 'location'
    },
    useBlobStorage (): boolean {
      return this.settings.previewMode ||
        this.settings.useBlobStorage
    },
    defaultLanguage (): DeepReadonly<SupportedLanguage> {
      return this.languages.find(lang => lang.isDefault) || this.languages[0] || { symbol: 'en' }
    },
  },

  actions: {
    async _internalLoadData (jsonUrl: string): Promise<AllData> {
      const res = await fetch(`${jsonUrl}`)
      if (!res.ok) {
        console.log(`Data response was not ok: ${res.statusText}`)
        throw new Error('Failed to load data')
      }
      return await res.json()
    },
    /**
     * @returns `true` if the data was loaded successfully, `false` if not
     */
    async loadData (): Promise<boolean> {
      let data: AllData | null = null
      let attempts = 0
      const loader$ = document.getElementById('data-loader')
      const branchError$ = document.getElementById('branch-error')
      const dataError$ = document.getElementById('data-error')
      while (!data) {
        try {
          const branchDataRes = await getBranchData()
          if (!branchDataRes.isDeployed) {
            /* istanbul ignore else @preserve */
            if (loader$) loader$.style.display = 'none'
            /* istanbul ignore else @preserve */
            if (branchError$) branchError$.style.display = 'block'
            const branchErrorUndeployed = document.getElementById('branch-error-undeployed')
            /* istanbul ignore else @preserve */
            if (branchErrorUndeployed) branchErrorUndeployed.style.display = 'block'
            return false
          }
          if (branchDataRes.jsonUrl === 'Access Denied') {
            /* istanbul ignore else @preserve */
            if (loader$) loader$.style.display = 'none'
            /* istanbul ignore else @preserve */
            if (branchError$) branchError$.style.display = 'block'
            const branchErrorIp = document.getElementById('branch-error-ip')
            /* istanbul ignore else @preserve */
            if (branchErrorIp) branchErrorIp.style.display = 'block'
            const branchErrorIpSpan = document.getElementById('branch-error-ip-span')
            /* istanbul ignore else @preserve */
            if (branchErrorIpSpan) branchErrorIpSpan.innerText = branchDataRes.ipAddress || 'Unknown'
            return false
          }
          data = await this._internalLoadData(branchDataRes.jsonUrl)
          this.displaySyncUrl = branchDataRes.displaySyncUrl
          this.token = branchDataRes.token ?? ''
          this.serviceWorkerUrl = data.settings.previewMode
            ? branchDataRes.jsonUrl.replace(/(data\/)?data\.json.*/, 'sw.js')
            : '/sw.js'
        } catch (err) {
          console.log(err)
          if (++attempts >= 3) {
            console.log('Failed to load data 3 times, showing user error')
            /* istanbul ignore else @preserve */
            if (loader$) loader$.style.display = 'none'
            /* istanbul ignore else @preserve */
            if (dataError$) dataError$.style.display = 'flex'
            return false
          } else {
            console.log(`Failed to load data ${attempts} times, retrying...`)
            await sleep(1000)
          }
        }
      }
      this.settings = readonly(data.settings)
      this.languageStrings = readonly(data.languageStrings as LanguageStrings[])
      this.languages = readonly(data.languages as SupportedLanguage[])
      this.languageExhibits = readonly(data.languageExhibits as LanguageExhibits[])
      this.languageTourCatalogs = readonly(data.languageTourCatalogs as LanguageTourCatalog[])
      this.languageSouvenirs = readonly(data.languageSouvenirs as LanguageSouvenirs[])
      return true
    },
    getMediaSrc (media: MediaAsset | null): string {
      if (!media) return ''

      if (this.useBlobStorage) {
        if (!media.sas) return ''
        const baseSas = media.sas.split('?')[0]
        return `${baseSas}${this.token}`
      } else {
        return `/${media.localPath}`
      }
    },
    /**
     * Refreshes the SAS token used for blob storage.
     */
    async refreshToken (): Promise<void> {
      try {
        const branchDataRes = await getBranchData()
        if (branchDataRes.token) {
          this.token = branchDataRes.token
        } else {
          console.log('No token returned from branch data, not updating token')
        }
      } catch (err) {
        console.log('Failed to refresh token:', err)
      }
    },
  },
})
